import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../Navbar';
import Footer from '../Footer';
import WaveDivider from '../home/WaveDivider';
import { useLanguage } from '../../contexts/LanguageContext';
import {
    SectionBadge,
    Doodles,
    ElementHeader,
    PageShell,
    IntroWave,
    HealthBlock,
} from './SobreNosotrosUI';

const SobreNosotrosPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { language } = useLanguage();
    const t = (es: string, en: string) => (language === 'es' ? es : en);

    useEffect(() => {
        if (location.hash) {
            const el = document.querySelector(location.hash);
            el?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [location.hash]);

    const scrollTo = (id: string) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    };

    const fiveElements = [
        {
            num: 1,
            id: 'elemento-filosofia',
            icon: '🧭',
            iconBg: 'bg-sky-100',
            title: t('Filosofía Princonser', 'Princonser Philosophy'),
            desc: t('La base de sabiduría, ética y pensamiento crítico.', 'The foundation of wisdom, ethics and critical thinking.'),
        },
        {
            num: 2,
            id: 'elemento-curriculum',
            icon: '💚',
            iconBg: 'bg-emerald-100',
            title: t('Currículum Holístico', 'Holistic Curriculum'),
            desc: t('Desarrollo mental, emocional, social, físico y financiero.', 'Mental, emotional, social, physical and financial development.'),
        },
        {
            num: 3,
            id: 'elemento-metodo',
            icon: '📝',
            iconBg: 'bg-amber-100',
            title: t('Método de Enseñanza', 'Teaching Method'),
            desc: t('Aprender leyendo, reflexionando, practicando y debatiendo.', 'Learn by reading, reflecting, practicing and debating.'),
        },
        {
            num: 4,
            id: 'elemento-gamificacion',
            icon: '🎮',
            iconBg: 'bg-princonser-purple-muted',
            title: t('Gamificación', 'Gamification'),
            desc: t('Retos, niveles y logros que motivan el avance.', 'Challenges, levels and achievements that motivate progress.'),
        },
        {
            num: 5,
            id: 'elemento-ecosistema',
            icon: '🛡️',
            iconBg: 'bg-rose-100',
            title: t('Ecosistema de Incentivos', 'Incentive Ecosystem'),
            desc: t('Insignias, progreso visible y participación familiar.', 'Badges, visible progress and family participation.'),
        },
    ];

    const filosofiaCards = [
        {
            letter: 'a',
            title: t('La brújula del aprendizaje', 'The learning compass'),
            desc: t('Invita al niño a cuestionar, descubrir y validar por sí mismo.', 'Invites the child to question, discover and validate for themselves.'),
            img: '/images/infosection/osito1.png',
        },
        {
            letter: 'b',
            title: t('Ética en acción', 'Ethics in action'),
            desc: t('No basta con saber; hay que hacer el bien y construir carácter.', 'Knowing is not enough; one must do good and build character.'),
            img: '/images/infosection/osito2.png',
        },
        {
            letter: 'c',
            title: t('Pensamiento dialéctico', 'Dialectical thinking'),
            desc: t('Ayuda a reconocer qué conservar y qué transformar.', 'Helps recognize what to keep and what to transform.'),
            img: '/images/infosection/osito3.png',
        },
        {
            letter: 'd',
            title: t('Cuatro áreas, un todo', 'Four areas, one whole'),
            desc: t('Mental-emocional, social-profesional, biológica-física y financiera.', 'Mental-emotional, social-professional, biological-physical and financial.'),
            img: '/images/infosection/osito4.png',
        },
    ];

    const mapaPilares = [
        { icon: '♥', title: t('Valores Universales', 'Universal Values'), desc: t('La ética como brújula diaria.', 'Ethics as a daily compass.') },
        { icon: '⚖️', title: t('Leyes Universales', 'Universal Laws'), desc: t('Principios eternos que rigen la vida.', 'Eternal principles that govern life.') },
        { icon: '🧭', title: t('Sabiduría Práctica', 'Practical Wisdom'), desc: t('Conocimiento que se aplica y transforma.', 'Knowledge that is applied and transforms.') },
        { icon: '👥', title: t('Dimensiones Humanas', 'Human Dimensions'), desc: t('Desarrollo integral del ser humano.', 'Integral development of the human being.') },
    ];

    const healthPills = [
        { id: 'salud-mental', label: t('Salud Mental', 'Mental Health'), icon: '🧠', bg: 'bg-princonser-purple-muted text-princonser-purple hover:bg-princonser-purple/20' },
        { id: 'salud-social', label: t('Salud Social', 'Social Health'), icon: '👥', bg: 'bg-princonser-orange-soft text-princonser-orange-dark hover:bg-orange-100' },
        { id: 'salud-fisica', label: t('Salud Física', 'Physical Health'), icon: '💪', bg: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' },
        { id: 'salud-financiera', label: t('Salud Financiera', 'Financial Health'), icon: '💰', bg: 'bg-amber-50 text-amber-800 hover:bg-amber-100' },
    ];

    const metodos = [
        { img: '/images/infosection/lecturaguiada.png', icon: '📖', iconColor: 'text-princonser-purple bg-princonser-purple-muted', title: t('Lectura Guiada', 'Guided Reading'), desc: t('Lectura interactiva y guiada para desarrollar comprensión lectora y pensamiento crítico.', 'Interactive guided reading to develop comprehension and critical thinking.') },
        { img: '/images/infosection/experimentosociales.png', icon: '👥', iconColor: 'text-emerald-600 bg-emerald-100', title: t('Experimentos Sociales', 'Social Experiments'), desc: t('Experimentos prácticos que fomentan la interacción social y el aprendizaje colaborativo.', 'Practical experiments that foster social interaction and collaborative learning.') },
        { img: '/images/infosection/reflexionaprendizaje.png', icon: '♥', iconColor: 'text-rose-600 bg-rose-100', title: t('Reflexión y Aprendizaje', 'Reflection and Learning'), desc: t('Momentos de reflexión profunda para asimilar y procesar el conocimiento adquirido.', 'Deep reflection to assimilate and process acquired knowledge.') },
        { img: '/images/infosection/aprendizajeproyectos.png', icon: '🧩', iconColor: 'text-orange-600 bg-orange-100', title: t('Aprendizaje por Proyectos', 'Project-Based Learning'), desc: t('Desarrollo de proyectos prácticos que integran múltiples áreas de conocimiento.', 'Practical projects integrating multiple areas of knowledge.') },
        { img: '/images/infosection/debateyjuegodebate.png', icon: '🎤', iconColor: 'text-princonser-purple bg-princonser-purple-muted', title: t('Debate y Juego de Debate', 'Debate and Debate Games'), desc: t('Actividades de debate estructurado que desarrollan comunicación y argumentación.', 'Structured debate activities that develop communication and argumentation.') },
        { img: '/images/infosection/tutoriapersonalizada.png', icon: '👤', iconColor: 'text-blue-600 bg-blue-100', title: t('Tutoría Personalizada', 'Personalized Tutoring'), desc: t('Acompañamiento individualizado adaptado al ritmo de cada niño.', 'Individualized support adapted to each child\'s pace.') },
    ];

    const gamificationSteps = [
        { n: 1, color: 'bg-princonser-purple', icon: '🎮', title: t('Juega y aprende', 'Play and learn'), desc: t('Completa actividades, juegos y misiones.', 'Complete activities, games and missions.') },
        { n: 2, color: 'bg-princonser-green', icon: '🛡️', title: t('Gana insignias', 'Earn badges'), desc: t('Obtén insignias por tus logros.', 'Earn badges for your achievements.') },
        { n: 3, color: 'bg-sky-500', icon: '📦', title: t('Suma puntos', 'Earn points'), desc: t('Acumula XP y sube de nivel.', 'Accumulate XP and level up.') },
        { n: 4, color: 'bg-princonser-orange', icon: '🎁', title: t('Recibe recompensas', 'Get rewards'), desc: t('Desbloquea recompensas y contenido exclusivo.', 'Unlock rewards and exclusive content.') },
    ];

    const gamificationDevelops = [
        { icon: '🧠', label: t('Motivación y constancia', 'Motivation and consistency') },
        { icon: '🎯', label: t('Autonomía y responsabilidad', 'Autonomy and responsibility') },
        { icon: '⭐', label: t('Confianza y autoestima', 'Confidence and self-esteem') },
        { icon: '💡', label: t('Pensamiento estratégico', 'Strategic thinking') },
        { icon: '♥', label: t('Valores y propósito', 'Values and purpose') },
    ];

    const ecosistemaCards = [
        {
            num: '01',
            color: 'from-princonser-gold to-princonser-orange',
            btn: 'bg-princonser-orange hover:bg-princonser-orange-dark',
            icon: '⭐',
            title: t('Recompensas por progreso', 'Progress rewards'),
            desc: t('Gana beneficios, puntos y recompensas mientras avanzas y participas en el ecosistema.', 'Earn benefits, points and rewards as you progress and participate in the ecosystem.'),
            cta: t('Ver recompensas', 'See rewards'),
            path: '/gamification',
            img: '/images/infosection/ecosistemainsentivos.png',
        },
        {
            num: '02',
            color: 'from-princonser-green to-emerald-600',
            btn: 'bg-princonser-green hover:bg-princonser-green-hover',
            icon: '👥',
            title: t('Compensación y referidos', 'Compensation and referrals'),
            desc: t('Recibe beneficios por recomendar, participar activamente y por tu antigüedad en la comunidad.', 'Receive benefits for referring, active participation and community seniority.'),
            cta: t('Descubrir beneficios', 'Discover benefits'),
            path: '/compensation',
            img: '/images/infosection/sistemacompensaciondiferidos.png',
        },
        {
            num: '03',
            color: 'from-princonser-purple-light to-princonser-purple',
            btn: 'bg-princonser-purple hover:bg-princonser-purple-dark',
            icon: '🚀',
            title: t('Financiamiento de proyectos', 'Project financing'),
            desc: t('Te ayudamos a impulsar tus ideas, metas y pequeños proyectos educativos o emprendedores.', 'We help you drive your ideas, goals and small educational or entrepreneurial projects.'),
            cta: t('Impulsar mi proyecto', 'Boost my project'),
            path: '/ecosistema-incentivo',
            img: '/images/infosection/financiamientoproyectos.png',
        },
    ];

    return (
        <PageShell>
            <Navbar />
            <IntroWave />

            <main>
                {/* Intro */}
                <section className="relative py-14 sm:py-20 bg-white">
                    <Doodles />
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="text-center mb-10">
                            <SectionBadge label={t('Sobre nosotros', 'About us')} className="mb-4" />
                            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-princonser-navy mb-4">
                                {t('¿Qué es', 'What is')}{' '}
                                <span className="text-princonser-purple">Princonser Kids</span>?
                            </h1>
                            <p className="text-princonser-text-body max-w-3xl mx-auto text-sm sm:text-base leading-relaxed">
                                {t(
                                    'Princonser Kids es un sistema educativo integral diseñado para formar niños sabios, éticos, resilientes y felices, con propósito y pensamiento crítico desde temprana edad.',
                                    'Princonser Kids is a comprehensive educational system designed to raise wise, ethical, resilient and happy children, with purpose and critical thinking from an early age.'
                                )}
                            </p>
                        </div>

                        <div className="max-w-4xl mx-auto bg-princonser-purple-muted border border-princonser-purple/15 rounded-2xl p-6 sm:p-8 text-center mb-12">
                            <span className="text-2xl mb-2 block" aria-hidden>
                                ⭐
                            </span>
                            <p className="font-bold text-princonser-navy text-base sm:text-lg">
                                {t(
                                    'Princonser Kids es un sistema de educación integral basado en 5 elementos principales.',
                                    'Princonser Kids is a comprehensive education system based on 5 main elements.'
                                )}
                            </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-5">
                            {fiveElements.map((el) => (
                                <button
                                    key={el.id}
                                    type="button"
                                    onClick={() => scrollTo(el.id)}
                                    className="relative bg-white rounded-2xl p-5 shadow-lg border border-gray-100 text-left hover:shadow-xl hover:border-princonser-purple/30 transition-all group"
                                >
                                    <span className="absolute top-3 left-3 w-7 h-7 rounded-full bg-princonser-purple text-white text-xs font-bold flex items-center justify-center">
                                        {el.num}
                                    </span>
                                    <div
                                        className={`w-14 h-14 rounded-full ${el.iconBg} flex items-center justify-center text-2xl mx-auto mt-6 mb-4 group-hover:scale-105 transition-transform`}
                                    >
                                        {el.icon}
                                    </div>
                                    <h3 className="font-bold text-princonser-navy text-sm text-center mb-2">{el.title}</h3>
                                    <p className="text-xs text-princonser-text-body text-center leading-relaxed">{el.desc}</p>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="mt-14">
                        <WaveDivider fillClass="text-princonser-surface" />
                    </div>
                </section>

                {/* Filosofía */}
                <section id="elemento-filosofia" className="scroll-mt-20 py-14 sm:py-20 bg-princonser-surface relative">
                    <Doodles />
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <ElementHeader
                            element={t('Elemento 1', 'Element 1')}
                            title={t('Filosofía Princonser', 'Princonser Philosophy')}
                            subtitle={t(
                                'La brújula que transforma el aprendizaje en pasión, propósito y poder personal.',
                                'The compass that transforms learning into passion, purpose and personal power.'
                            )}
                            description={t(
                                'Nuestra filosofía guía al niño a cuestionar, descubrir, actuar con valores y desarrollarse de manera integral. Es el corazón que da sentido a cada historia, actividad y decisión dentro del ecosistema Princonser Kids.',
                                'Our philosophy guides children to question, discover, act with values and develop integrally. It is the heart that gives meaning to every story, activity and decision within the Princonser Kids ecosystem.'
                            )}
                        />

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
                            {filosofiaCards.map((card) => (
                                <article key={card.letter} className="bg-white rounded-2xl p-5 shadow-md relative">
                                    <span className="absolute top-3 left-3 w-7 h-7 rounded-full bg-princonser-purple-muted text-princonser-purple text-sm font-bold flex items-center justify-center">
                                        {card.letter}
                                    </span>
                                    <img src={card.img} alt="" className="w-full h-36 object-contain my-4" />
                                    <h3 className="font-bold text-princonser-navy text-sm mb-2">{card.title}</h3>
                                    <p className="text-xs text-princonser-text-body">{card.desc}</p>
                                </article>
                            ))}
                        </div>

                        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg lg:col-span-2">
                            <h3 className="font-bold text-xl text-princonser-navy mb-2">{t('Mapa filosófico', 'Philosophical map')}</h3>
                            <p className="text-sm text-princonser-text-body mb-6">
                                {t(
                                    'Nuestra filosofía conecta cuatro pilares fundamentales que forman el carácter y la sabiduría del niño.',
                                    'Our philosophy connects four fundamental pillars that form the child\'s character and wisdom.'
                                )}
                            </p>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                {mapaPilares.map((p) => (
                                    <div key={p.title} className="text-center p-4 rounded-xl bg-princonser-purple-muted">
                                        <div className="text-2xl mb-2">{p.icon}</div>
                                        <p className="font-bold text-sm text-princonser-navy mb-1">{p.title}</p>
                                        <p className="text-xs text-princonser-text-body">{p.desc}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6 hidden lg:block">
                                <img
                                    src="/images/infosection/filosofiaprinconser.png"
                                    alt={t('Mapa filosófico Princonser', 'Princonser philosophical map')}
                                    className="w-full max-h-48 object-contain mx-auto"
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Currículum */}
                <section id="elemento-curriculum" className="scroll-mt-20 py-14 sm:py-20 bg-white relative">
                    <Doodles />
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <ElementHeader
                            element={t('Elemento 2', 'Element 2')}
                            title={t('Currículum Holístico', 'Holistic Curriculum')}
                            subtitle={t(
                                'Desarrollo integral en todas las dimensiones humanas para un crecimiento equilibrado.',
                                'Integral development in all human dimensions for balanced growth.'
                            )}
                            description={t(
                                'Princonser Kids acompaña a cada niño en su crecimiento mediante un currículo que integra salud mental, social, física y financiera, formando hábitos, criterio y bienestar para la vida real.',
                                'Princonser Kids accompanies each child through a curriculum integrating mental, social, physical and financial health, building habits, judgment and well-being for real life.'
                            )}
                        />

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
                            {healthPills.map((pill) => (
                                <button
                                    key={pill.id}
                                    type="button"
                                    onClick={() => scrollTo(pill.id)}
                                    className={`flex items-center gap-3 px-5 py-4 rounded-2xl font-semibold text-sm transition-colors ${pill.bg}`}
                                >
                                    <span className="text-2xl">{pill.icon}</span>
                                    {pill.label}
                                </button>
                            ))}
                        </div>

                        <div className="space-y-8">
                            <HealthBlock
                                id="salud-mental"
                                title={t('Salud Mental', 'Mental Health')}
                                description={t(
                                    'Desarrollo del pensamiento crítico, inteligencia emocional y bienestar mental. Aprende a gestionar emociones, desarrollar resiliencia y mantener un estado mental saludable.',
                                    'Development of critical thinking, emotional intelligence and mental well-being. Learn to manage emotions, build resilience and maintain a healthy mental state.'
                                )}
                                image="/images/infosection/saludmental.png"
                                theme="purple"
                                features={[
                                    { icon: '🧠', label: t('Pensamiento crítico', 'Critical thinking') },
                                    { icon: '♥', label: t('Gestión emocional', 'Emotional management') },
                                    { icon: '🛡️', label: t('Resiliencia', 'Resilience') },
                                ]}
                            />
                            <HealthBlock
                                id="salud-social"
                                title={t('Salud Social', 'Social Health')}
                                description={t(
                                    'Habilidades de comunicación, liderazgo y competencias sociales. Desarrolla relaciones saludables, trabajo en equipo y habilidades sociales efectivas.',
                                    'Communication, leadership and social skills. Develop healthy relationships, teamwork and effective social skills.'
                                )}
                                image="/images/infosection/saludsocial.png"
                                theme="orange"
                                imageRight
                                features={[
                                    { icon: '💬', label: t('Comunicación', 'Communication') },
                                    { icon: '👥', label: t('Trabajo en equipo', 'Teamwork') },
                                    { icon: '🚩', label: t('Liderazgo', 'Leadership') },
                                ]}
                            />
                            <HealthBlock
                                id="salud-fisica"
                                title={t('Salud Física', 'Physical Health')}
                                description={t(
                                    'Bienestar físico, nutrición y desarrollo biológico saludable. Fomenta hábitos que ayudan a mantener un cuerpo fuerte, activo y en equilibrio.',
                                    'Physical wellness, nutrition and healthy biological development. Promotes habits for a strong, active and balanced body.'
                                )}
                                image="/images/infosection/saludfisica.png"
                                theme="green"
                                features={[
                                    { icon: '💪', label: t('Fuerza física', 'Physical strength') },
                                    { icon: '🍎', label: t('Nutrición', 'Nutrition') },
                                    { icon: '🌿', label: t('Hábitos saludables', 'Healthy habits') },
                                ]}
                            />
                            <HealthBlock
                                id="salud-financiera"
                                title={t('Salud Financiera', 'Financial Health')}
                                description={t(
                                    'Inteligencia financiera, ahorro y gestión responsable del dinero. Aprende a manejar recursos, planificar y crear riqueza de manera sostenible.',
                                    'Financial intelligence, saving and responsible money management. Learn to manage resources, plan and build wealth sustainably.'
                                )}
                                image="/images/infosection/saludfinanciera.png"
                                theme="gold"
                                imageRight
                                features={[
                                    { icon: '👛', label: t('Gestión del dinero', 'Money management') },
                                    { icon: '📅', label: t('Planificación', 'Planning') },
                                    { icon: '📈', label: t('Inversión', 'Investment') },
                                ]}
                            />
                        </div>
                    </div>
                    <div className="mt-12">
                        <WaveDivider fillClass="text-princonser-surface" />
                    </div>
                </section>

                {/* Método */}
                <section id="elemento-metodo" className="scroll-mt-20 py-14 sm:py-20 bg-princonser-surface relative">
                    <Doodles />
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <ElementHeader
                            element={t('Elemento 3 del sistema', 'Element 3 of the system')}
                            title={t('Método de Enseñanza', 'Teaching Method')}
                            subtitle={t(
                                'En Princonser Kids combinamos estrategias y técnicas para que los niños aprendan, apliquen y retengan el conocimiento con todos sus sentidos.',
                                'At Princonser Kids we combine strategies and techniques so children learn, apply and retain knowledge with all their senses.'
                            )}
                        />

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                            {metodos.map((m) => (
                                <article key={m.title} className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100">
                                    <div className="h-44 bg-gray-50 flex items-center justify-center p-4">
                                        <img src={m.img} alt="" className="max-h-full object-contain" />
                                    </div>
                                    <div className="p-5">
                                        <div className={`inline-flex w-9 h-9 rounded-full items-center justify-center text-lg mb-3 ${m.iconColor}`}>
                                            {m.icon}
                                        </div>
                                        <h3 className="font-bold text-princonser-navy mb-2">{m.title}</h3>
                                        <p className="text-sm text-princonser-text-body">{m.desc}</p>
                                    </div>
                                </article>
                            ))}
                        </div>

                        <div className="bg-princonser-purple-muted rounded-2xl px-6 py-4 flex items-center justify-center gap-3 text-center">
                            <span aria-hidden>⭐</span>
                            <p className="text-sm font-medium text-princonser-purple">
                                {t(
                                    'Aprender, aplicar, reflexionar, debatir y crecer con acompañamiento.',
                                    'Learn, apply, reflect, debate and grow with support.'
                                )}
                            </p>
                            <span aria-hidden>💜</span>
                        </div>
                    </div>
                </section>

                {/* Gamificación */}
                <section id="elemento-gamificacion" className="scroll-mt-20 py-14 sm:py-20 bg-white relative">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <ElementHeader
                            element={t('Elemento 4', 'Element 4')}
                            title={t('Gamificación', 'Gamification')}
                            subtitle={t('Aprender jugando, crecer con propósito.', 'Learn by playing, grow with purpose.')}
                            description={t(
                                'Nuestro sistema lúdico convierte logros en aventuras: juegos, retos y recompensas que motivan al niño a seguir aprendiendo con entusiasmo.',
                                'Our playful system turns achievements into adventures: games, challenges and rewards that motivate children to keep learning with enthusiasm.'
                            )}
                        />

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-12">
                            <div className="rounded-2xl overflow-hidden bg-princonser-purple-muted p-4">
                                <img
                                    src="/images/infosection/gamificacion.png"
                                    alt={t('App Princonser Kids', 'Princonser Kids App')}
                                    className="w-full h-auto object-contain"
                                />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-princonser-purple mb-6">{t('¿Cómo funciona?', 'How does it work?')}</h3>
                                <div className="space-y-4">
                                    {gamificationSteps.map((step, i) => (
                                        <div key={step.n} className="flex items-start gap-4">
                                            <div
                                                className={`w-10 h-10 rounded-full ${step.color} text-white font-bold flex items-center justify-center flex-shrink-0`}
                                            >
                                                {step.n}
                                            </div>
                                            <div>
                                                <p className="font-bold text-princonser-navy flex items-center gap-2">
                                                    <span>{step.icon}</span> {step.title}
                                                </p>
                                                <p className="text-sm text-princonser-text-body">{step.desc}</p>
                                            </div>
                                            {i < gamificationSteps.length - 1 && (
                                                <span className="hidden sm:block text-gray-300 ml-auto">→</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => navigate('/consejo-sabios')}
                                    className="mt-8 px-6 py-3 rounded-full bg-princonser-purple text-white font-bold hover:bg-princonser-purple-dark transition-colors"
                                >
                                    {t('Explorar App Debate', 'Explore Debate App')} →
                                </button>
                            </div>
                        </div>

                        <div className="bg-princonser-purple-muted rounded-2xl p-6 sm:p-8">
                            <h3 className="text-center font-bold text-princonser-navy mb-6">{t('¿Qué desarrollamos?', 'What do we develop?')}</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                                {gamificationDevelops.map((item) => (
                                    <div key={item.label} className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm">
                                        <span className="text-2xl">{item.icon}</span>
                                        <span className="text-sm font-semibold text-princonser-navy">{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="mt-12">
                        <WaveDivider fillClass="text-princonser-cream" />
                    </div>
                </section>

                {/* Ecosistema */}
                <section id="elemento-ecosistema" className="scroll-mt-20 py-14 sm:py-20 bg-princonser-cream relative">
                    <Doodles />
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="text-center mb-10">
                            <SectionBadge label={t('Elemento 5', 'Element 5')} className="mb-4" />
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-princonser-navy">
                                {t('Ecosistema de', 'Ecosystem of')}{' '}
                                <span className="text-princonser-orange">{t('incentivos', 'incentives')}</span>
                            </h2>
                            <p className="text-princonser-text-body mt-3 max-w-2xl mx-auto">
                                {t(
                                    'Recompensamos el progreso, impulsamos la participación y apoyamos el crecimiento de los niños y sus familias.',
                                    'We reward progress, encourage participation and support the growth of children and their families.'
                                )}
                            </p>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
                            {ecosistemaCards.map((card) => (
                                <article key={card.num} className="bg-white rounded-3xl p-6 shadow-xl relative overflow-hidden">
                                    <span
                                        className={`absolute top-4 right-4 w-10 h-10 rounded-full bg-gradient-to-br ${card.color} text-white font-bold flex items-center justify-center text-sm`}
                                    >
                                        {card.num}
                                    </span>
                                    <div className="h-40 flex items-center justify-center mb-4">
                                        <img src={card.img} alt="" className="max-h-full object-contain" />
                                    </div>
                                    <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${card.color} text-white flex items-center justify-center text-xl mb-4`}>
                                        {card.icon}
                                    </div>
                                    <h3 className="font-bold text-lg text-princonser-navy mb-2">{card.title}</h3>
                                    <p className="text-sm text-princonser-text-body mb-6">{card.desc}</p>
                                    <button
                                        type="button"
                                        onClick={() => navigate(card.path)}
                                        className={`w-full py-3 rounded-full text-white font-semibold text-sm ${card.btn} transition-colors`}
                                    >
                                        {card.cta} →
                                    </button>
                                </article>
                            ))}
                        </div>

                        <div className="flex flex-wrap justify-center gap-8 sm:gap-12 py-6 border-t border-princonser-orange/20">
                            {[
                                { icon: '✓', label: t('Seguro y confiable', 'Safe and reliable') },
                                { icon: '♥', label: t('Hecho para familias', 'Made for families') },
                                { icon: '⭐', label: t('Impulsa su futuro', 'Boosts their future') },
                            ].map((item) => (
                                <div key={item.label} className="flex items-center gap-2 text-princonser-navy font-medium text-sm">
                                    <span className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center">
                                        {item.icon}
                                    </span>
                                    {item.label}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </PageShell>
    );
};

export default SobreNosotrosPage;
